import { merge, Label } from '@repo/ui'
import { ErrorMessage, useField, UseFieldProps } from 'formik'
import { observer } from 'mobx-react-lite'
import React from 'react'

import { FieldError } from './FieldError'
import { useForwardedRef } from '../../hooks/useForwardedRef'

interface TextFieldProps extends UseFieldProps {
  autoFocus?: boolean
  disableSelectOnFocus?: boolean
  isDisabled?: boolean
  label: React.ReactNode
  maxLength?: number
  min?: number
  onBlur?: (event: React.FocusEvent) => void
  onKeyPress?: (event: React.KeyboardEvent) => void
  placeholder?: string
  type?: 'text' | 'email' | 'password' | 'number'
}

const TextFieldWithRef = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      autoFocus,
      disableSelectOnFocus = false,
      isDisabled = false,
      label,
      maxLength,
      min = 0,
      onKeyPress,
      placeholder,
      type = 'text',
      ...props
    }: TextFieldProps,
    ref
  ) => {
    const [field, meta, helpers] = useField(props)
    const forwardedRef = useForwardedRef(ref)

    const handleFocus = () => {
      if (disableSelectOnFocus) return
      forwardedRef.current?.select()
    }

    const handleBlur = (event: React.FocusEvent) => {
      field.onBlur(event)
      props.onBlur?.(event)
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (type === 'number') {
        const { value } = event.target
        const numberValue = value === '' ? undefined : parseInt(value, 10)
        helpers.setValue(numberValue)
      } else {
        field.onChange(event)
      }
    }

    return (
      <div className='flex flex-1 flex-col'>
        <div className='flex flex-col gap-3'>
          <Label>
            <span>{label}</span>
            {maxLength && <InputMaxLength maxLength={maxLength} value={String(field.value)} />}
          </Label>

          <input
            {...field}
            autoFocus={autoFocus}
            className={merge('input', {
              'has-error border-red-500 focus:ring-0': meta.error
            })}
            disabled={isDisabled}
            min={min}
            placeholder={placeholder}
            ref={forwardedRef}
            type={type}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyPress={onKeyPress}
          />
        </div>

        <ErrorMessage {...field} component={FieldError} />
      </div>
    )
  }
)

TextFieldWithRef.displayName = 'TextField'

// eslint-disable-next-line mobx/no-anonymous-observer
export const TextField = observer(TextFieldWithRef)

interface MaxLengthProps {
  className?: string
  maxLength?: number
  value: string
}

export const InputMaxLength = observer(({ className, maxLength, value }: MaxLengthProps) => {
  const getTextColorStyle = (length: number, maxLength: number) => {
    switch (true) {
      case length > maxLength - 5: {
        return { color: `var(--red-500)` }
      }

      case length > maxLength - 10: {
        return { color: `var(--yellow-500)` }
      }

      default: {
        return {}
      }
    }
  }

  const getLength = (value: string) => {
    return value.length
  }

  if (!maxLength) return null

  return (
    <div className={merge('inline-flex justify-end text-sm text-muted', className)}>
      <span style={getTextColorStyle(getLength(value), maxLength)}>{getLength(value)}</span>
      <span>/</span>
      <span>{maxLength}</span>
    </div>
  )
})
